/**
 * runPrefill — the `workflow_prefill` trigger → poll → store → quarantine
 * cycle, extracted verbatim from `App.tsx::runPrefillFetch`,
 * `ui/apps/outlook-addin/src/App.tsx` 367–453.
 *
 * Order matters and is preserved exactly:
 *
 *   1. TRIGGER — `reanalysePrefill`-shaped (fresh idempotency key, App.tsx
 *      383–390) when `fresh`, else `triggerPrefill`-shaped (hash-keyed reuse,
 *      391–397).
 *   2. POLL — `io.pollPrefill(instanceId)`.
 *   3. CANCEL CHECK — `if (cancel.cancelled) return;` (App.tsx 399), right
 *      after the poll resolves and BEFORE the store call. A prefill whose
 *      drawer closed (or whose client changed) mid-poll must not write a
 *      stale value onto the analysis row.
 *   4. STORE — RAW confidence, not quarantined (App.tsx 402–414: "field_confidence
 *      rides RAW — the store is the model's output; quarantining is a display
 *      rule for THIS render only", story 26727). Fire-and-forget / best-effort,
 *      matching `orchestratorApi.storeBriefPrefill`'s own contract ("a failure
 *      must not block the form") — errors are swallowed, not surfaced to the
 *      caller.
 *   5. QUARANTINE — `quarantineClientDerivedPrefill` strips the client-derived
 *      fields (`CLIENT_DERIVED_PREFILL_KEYS`: `policy_number` / `policy_type` /
 *      `carrier_name` / `carrier_id` / `policy_type_code` / `clientlookup_code`)
 *      from BOTH `values` and `confidence` when `clientSk === null` (App.tsx
 *      416–433: "a prefill that ran with NO client selected skipped the
 *      server's TVF policy resolution, so its policy_number/policy_type/
 *      carrier_* are the email's RAW guesses"). Deliberately AFTER the store —
 *      store is a data write of the model's real output, quarantine is a
 *      display rule for this render only.
 *
 * Returns `null` when cancelled after the poll resolves — the caller
 * (App.tsx's `setDrawerState` merge, or ora's equivalent) must not apply a
 * result for a prefill it no longer wants.
 *
 * `messageId` / `mailbox` also ride into `buildPrefillInputs` as
 * `internet_message_id` / `mailbox_address` (story 26752 / ADO 28067 item 8):
 * identity that lets `workflow_prefill` recover a blank `source_document_text`
 * / `attachment_text` from the stored `email_analysis` row when a caller has
 * no document to extract from at all: ora's triage "I sent it" /
 * prepared-brief launch, and the add-in's brief opened from the Today list
 * whose email is not the item selected in Outlook — neither has a mail item
 * body to read. Included only when non-empty, so a caller that never supplies
 * them keeps sending the exact payload it always has. Recovery only fills a
 * field the caller left BLANK, so an add-in prefill with a real Office body
 * keeps extracting from that body.
 *
 * Pure: no React, no Office.js, no network — `io` is the only side-effecting
 * surface, and it is injected.
 */
import { quarantineClientDerivedPrefill } from "~/common/workflows/launch/policyPrefillOptions";
import type { LaunchIO, PrefillConfidence } from "~/common/workflows/launch/core/launchIO";
import { buildPrefillInputs, hashPrefillInputs } from "~/common/workflows/launch/core/prefillInputs";

/**
 * `{cancelled: boolean}` — the add-in's mutable cancel-ref shape (App.tsx
 * 355: `prefillCancelRef`) — or a `() => boolean` predicate for a host that
 * prefers a function over a mutable ref (e.g. one reading React state via a
 * ref-backed getter). Checked once, right after the poll resolves.
 */
/**
 * Does a brief already carry its filled-in form? (story 28795)
 *
 * A brief's stored `prefill_values` is always a COMPLETE `workflow_prefill`
 * result: the add-in and ora store a whole run's values, and since story 28795
 * ambient intake computes and stores one when it prepares the brief, writing a
 * complete result or nothing. So any non-empty stored prefill means opening the
 * brief needs no live prefill. ONE rule for both surfaces.
 */
export function hasStoredPrefill(
  storedPrefillValues: Record<string, unknown> | null | undefined,
): boolean {
  return Boolean(storedPrefillValues) && Object.keys(storedPrefillValues ?? {}).length > 0;
}

export type PrefillCancelSignal = { cancelled: boolean } | (() => boolean);

function isCancelled(cancel: PrefillCancelSignal): boolean {
  return typeof cancel === "function" ? cancel() : cancel.cancelled;
}

export interface RunPrefillArgs {
  io: LaunchIO;
  workflowId: string;
  clientSk: number | null;
  sourceText: string;
  alreadyExtracted?: Record<string, unknown>;
  attachmentText?: string | null;
  /** `true` only for RE-ANALYSE — a fresh idempotency key so an unchanged
   *  re-press does not return the same stale completed instance (PAD-4). */
  fresh?: boolean;
  /** The email's RFC 5322 Message-ID. `storeBriefPrefill` is skipped when
   *  this is blank — there is no analysis row to cache onto. */
  messageId?: string | null;
  /** See module docstring — rides into `inputs.mailbox_address` via
   *  `buildPrefillInputs`, included only when non-empty. */
  mailbox?: string | null;
  cancel: PrefillCancelSignal;
}

export interface RunPrefillResult {
  values: Record<string, unknown>;
  confidence: Record<string, PrefillConfidence> | undefined;
}

export async function runPrefill(
  args: RunPrefillArgs,
): Promise<RunPrefillResult | null> {
  const {
    io,
    workflowId,
    clientSk,
    sourceText,
    alreadyExtracted,
    attachmentText,
    fresh = false,
    messageId,
    mailbox,
    cancel,
  } = args;

  const inputs = buildPrefillInputs(
    workflowId,
    clientSk,
    sourceText,
    alreadyExtracted,
    attachmentText ?? undefined,
    messageId ?? undefined,
    mailbox ?? undefined,
  );
  // The ONE difference between a normal trigger and RE-ANALYSE: the key.
  // Reusing the hashed key on a re-press would return the prior COMPLETED
  // instance with the prior values — a re-analyse that appears to work and
  // changes nothing.
  const idempotencyKey = fresh
    ? `prefill:${hashPrefillInputs(inputs)}:reanalyse:${Date.now()}`
    : `prefill:${hashPrefillInputs(inputs)}`;

  const { instanceId } = await io.triggerPrefill(inputs, idempotencyKey);
  const result = await io.pollPrefill(instanceId);

  if (isCancelled(cancel)) return null;

  const mid = (messageId ?? "").trim();
  if (mid) {
    // Best-effort — a failure here must not surface to the caller (matches
    // `orchestratorApi.storeBriefPrefill`'s documented contract).
    void io
      .storeBriefPrefill({
        messageIdentity: mid,
        prefillValues: result.values ?? {},
        workflowId,
        fieldConfidence: result.confidence,
      })
      .catch(() => {});
  }

  const values =
    clientSk === null
      ? quarantineClientDerivedPrefill(result.values ?? {})
      : (result.values ?? {});
  const confidence =
    clientSk === null && result.confidence
      ? (quarantineClientDerivedPrefill(
          result.confidence as Record<string, unknown>,
        ) as Record<string, PrefillConfidence>)
      : result.confidence;

  return { values, confidence };
}
